import { parseCalculatorUserOverlayDocumentV4 } from "../js/calculator-user-layer-v4-contract.js";
import { decodeCalculatorPresetPayload } from "../js/presets/collection-contracts.js";
import { browserPresetWorkspace } from "../js/presets/browser-workspace.js";
import {
  loadCalculatorLootConditionContextResponseV1,
  parseCalculatorLootConditionContextResponseV1,
} from "./map-zone-loot-summary.js";

export const CALCULATOR_MAP_CONTEXT_PROJECTION_VERSION = 1;
export const CALCULATOR_MAP_PRESET_COLLECTION_KEY = "calculator-presets";
export const CALCULATOR_MAP_OVERLAY_COLLECTION_KEY = "calculator-overlays-v4";

const OWNER_STATES = new Set([
  "conflict",
  "invalid",
  "loading",
  "locked",
  "missing",
  "pending",
  "ready",
  "unavailable",
  "unsupportedVersion",
]);

function isPlainObject(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === null || prototype === Object.prototype;
}

function exactObject(value, fields, path) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${path} must be a plain object`);
  }
  const expected = [...fields].sort();
  const actual = Object.keys(value).sort();
  if (
    expected.length !== actual.length
    || expected.some((field, index) => field !== actual[index])
  ) {
    throw new TypeError(`${path} has unsupported or missing fields`);
  }
  return value;
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

function boundedString(value, path, { nullable = false } = {}) {
  if (nullable && value === null) {
    return null;
  }
  if (
    typeof value !== "string"
    || !value
    || value.trim() !== value
    || value.length > 512
  ) {
    throw new TypeError(`${path} must be a bounded non-empty string`);
  }
  return value;
}

function nullableUnsignedInteger(value, path, maximum) {
  if (value === null) {
    return null;
  }
  if (!Number.isSafeInteger(value) || value < 0 || value > maximum) {
    throw new TypeError(`${path} must be an unsigned integer`);
  }
  return value;
}

function presetUnsignedNumber(value, path, maximum) {
  if (!Number.isSafeInteger(value) || value < 0 || value > maximum) {
    throw new TypeError(`${path} must be an unsigned integer`);
  }
  return value;
}

function presetUnsignedDecimalString(value, path, maximum) {
  if (typeof value !== "string" || !/^(?:0|[1-9]\d*)$/.test(value)) {
    throw new TypeError(`${path} must be a canonical unsigned decimal string`);
  }
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed > maximum) {
    throw new TypeError(`${path} must be an unsigned integer`);
  }
  return parsed;
}

function boundedOverlayDocument(value) {
  return parseCalculatorUserOverlayDocumentV4(value);
}

function ownerState(value, path) {
  if (!OWNER_STATES.has(value)) {
    throw new TypeError(`${path} has an unsupported owner state`);
  }
  return value;
}

function parseCalculatorProjection(value) {
  const calculator = exactObject(value, [
    "fishingLifeskillLevel",
    "fishingMastery",
    "reason",
    "resolvedRevision",
    "source",
    "state",
    "workspaceRevision",
  ], "calculatorMapContext.calculator");
  const state = ownerState(calculator.state, "calculatorMapContext.calculator.state");
  const source = calculator.source === null
    ? null
    : calculator.source === "serverDefault" || calculator.source === "workspaceCurrent"
      ? calculator.source
      : (() => { throw new TypeError("calculatorMapContext.calculator.source is invalid"); })();
  const parsed = {
    state,
    source,
    resolvedRevision: boundedString(
      calculator.resolvedRevision,
      "calculatorMapContext.calculator.resolvedRevision",
      { nullable: true },
    ),
    workspaceRevision: boundedString(
      calculator.workspaceRevision,
      "calculatorMapContext.calculator.workspaceRevision",
      { nullable: true },
    ),
    fishingMastery: nullableUnsignedInteger(
      calculator.fishingMastery,
      "calculatorMapContext.calculator.fishingMastery",
      4_294_967_295,
    ),
    fishingLifeskillLevel: nullableUnsignedInteger(
      calculator.fishingLifeskillLevel,
      "calculatorMapContext.calculator.fishingLifeskillLevel",
      65_535,
    ),
    reason: boundedString(
      calculator.reason,
      "calculatorMapContext.calculator.reason",
      { nullable: true },
    ),
  };
  if (
    state === "ready"
    && (
      parsed.source === null
      || parsed.resolvedRevision === null
      || parsed.fishingMastery === null
      || parsed.fishingLifeskillLevel === null
      || parsed.reason !== null
    )
  ) {
    throw new TypeError("Ready calculator map context is incomplete");
  }
  if (state !== "ready" && (
    parsed.source !== null
    || parsed.resolvedRevision !== null
    || parsed.fishingMastery !== null
    || parsed.fishingLifeskillLevel !== null
  )) {
    throw new TypeError("Non-ready calculator map context contains authoritative values");
  }
  return parsed;
}

function parseOverlayProjection(value) {
  const overlay = exactObject(value, [
    "document",
    "reason",
    "state",
    "workspaceRevision",
  ], "calculatorMapContext.overlay");
  const state = ownerState(overlay.state, "calculatorMapContext.overlay.state");
  const parsed = {
    state,
    workspaceRevision: boundedString(
      overlay.workspaceRevision,
      "calculatorMapContext.overlay.workspaceRevision",
      { nullable: true },
    ),
    document: overlay.document === null ? null : boundedOverlayDocument(overlay.document),
    reason: boundedString(
      overlay.reason,
      "calculatorMapContext.overlay.reason",
      { nullable: true },
    ),
  };
  if (
    state === "ready"
    && (parsed.workspaceRevision === null || parsed.document === null || parsed.reason !== null)
  ) {
    throw new TypeError("Ready calculator overlay projection is incomplete");
  }
  if (state !== "ready" && parsed.document !== null) {
    throw new TypeError("Non-ready calculator overlay projection contains a document");
  }
  if (state === "missing" && (parsed.workspaceRevision !== null || parsed.reason !== null)) {
    throw new TypeError("Missing calculator overlay projection is not canonical");
  }
  return parsed;
}

export function parseCalculatorMapContextProjectionV1(value) {
  const projection = exactObject(value, [
    "calculator",
    "overlay",
    "revision",
    "schemaVersion",
    "state",
  ], "calculatorMapContext");
  if (projection.schemaVersion !== CALCULATOR_MAP_CONTEXT_PROJECTION_VERSION) {
    throw new TypeError("calculatorMapContext.schemaVersion must be exactly 1");
  }
  if (!Number.isSafeInteger(projection.revision) || projection.revision < 0) {
    throw new TypeError("calculatorMapContext.revision must be a non-negative integer");
  }
  const parsed = {
    schemaVersion: CALCULATOR_MAP_CONTEXT_PROJECTION_VERSION,
    revision: projection.revision,
    state: ownerState(projection.state, "calculatorMapContext.state"),
    calculator: parseCalculatorProjection(projection.calculator),
    overlay: parseOverlayProjection(projection.overlay),
  };
  if (
    parsed.state === "ready"
    && (
      parsed.calculator.state !== "ready"
      || !["ready", "missing"].includes(parsed.overlay.state)
    )
  ) {
    throw new TypeError("Ready calculator map projection contains a non-ready owner");
  }
  return deepFreeze(parsed);
}

function loadingCalculator() {
  return {
    state: "loading",
    source: null,
    resolvedRevision: null,
    workspaceRevision: null,
    fishingMastery: null,
    fishingLifeskillLevel: null,
    reason: "calculator-context-loading",
  };
}

function loadingOverlay() {
  return {
    state: "loading",
    workspaceRevision: null,
    document: null,
    reason: "calculator-overlay-loading",
  };
}

function failedCalculator(state, reason) {
  return {
    ...loadingCalculator(),
    state,
    reason,
  };
}

function failedOverlay(state, reason) {
  return {
    ...loadingOverlay(),
    state,
    reason,
  };
}

function repositoryFailureState(value) {
  return [
    "conflict",
    "invalid",
    "locked",
    "pending",
    "unavailable",
    "unsupportedVersion",
  ].includes(value) ? value : "unavailable";
}

function overallState(calculator, overlay) {
  if (calculator.state === "ready" && ["ready", "missing"].includes(overlay.state)) {
    return "ready";
  }
  for (const state of [
    "unsupportedVersion",
    "invalid",
    "locked",
    "unavailable",
    "conflict",
    "pending",
  ]) {
    if (calculator.state === state || overlay.state === state) {
      return state;
    }
  }
  return "loading";
}

function requireWorkspace(value) {
  const workspace = value ?? browserPresetWorkspace();
  for (const method of ["load", "state", "subscribe"]) {
    if (typeof workspace?.[method] !== "function") {
      throw new TypeError(`Calculator map context workspace is missing ${method}()`);
    }
  }
  return workspace;
}

function canonicalJson(value) {
  return JSON.stringify(value);
}

export class CalculatorMapContextProjectionV1Owner {
  constructor({
    workspace = null,
    loadServerContext = loadCalculatorLootConditionContextResponseV1,
    onProjection = () => {},
  } = {}) {
    if (typeof loadServerContext !== "function") {
      throw new TypeError("Calculator map context requires a server context loader");
    }
    if (typeof onProjection !== "function") {
      throw new TypeError("Calculator map context projection callback must be a function");
    }
    this._workspace = requireWorkspace(workspace);
    this._loadServerContext = loadServerContext;
    this._onProjection = onProjection;
    this._calculatorWorkspace = { kind: "loading" };
    this._overlay = loadingOverlay();
    this._server = { kind: "loading", value: null };
    this._projection = parseCalculatorMapContextProjectionV1({
      schemaVersion: 1,
      revision: 0,
      state: "loading",
      calculator: loadingCalculator(),
      overlay: this._overlay,
    });
    this._started = false;
    this._disposed = false;
    this._requestGeneration = 0;
    this._request = null;
    this._unsubscribers = [];
    this.ready = Promise.resolve(this._projection);
  }

  current() {
    return this._projection;
  }

  _calculatorProjection() {
    const current = this._calculatorWorkspace;
    if (current.kind === "loading") {
      return failedCalculator("loading", "calculator-current-loading");
    }
    if (current.kind !== "ready" && current.kind !== "missing") {
      return failedCalculator(
        repositoryFailureState(current.kind),
        `calculator-current-${repositoryFailureState(current.kind)}`,
      );
    }
    if (this._server.kind !== "ready") {
      return failedCalculator(
        this._server.kind === "loading" ? "loading" : "unavailable",
        this._server.kind === "loading"
          ? "calculator-context-loading"
          : "calculator-context-unavailable",
      );
    }
    if (current.kind === "missing") {
      return {
        state: "ready",
        source: "serverDefault",
        resolvedRevision: this._server.value.resolvedRevision,
        workspaceRevision: null,
        fishingMastery: this._server.value.fishingMastery,
        fishingLifeskillLevel: this._server.value.fishingLifeskillLevel,
        reason: null,
      };
    }
    return {
      state: "ready",
      source: "workspaceCurrent",
      resolvedRevision: this._server.value.resolvedRevision,
      workspaceRevision: current.revision,
      fishingMastery: current.fishingMastery,
      fishingLifeskillLevel: current.fishingLifeskillLevel,
      reason: null,
    };
  }

  _publish() {
    if (this._disposed) {
      return this._projection;
    }
    const calculator = this._calculatorProjection();
    const candidate = {
      schemaVersion: 1,
      revision: this._projection.revision + 1,
      state: overallState(calculator, this._overlay),
      calculator,
      overlay: this._overlay,
    };
    const comparable = { ...candidate, revision: this._projection.revision };
    if (canonicalJson(comparable) === canonicalJson(this._projection)) {
      return this._projection;
    }
    this._projection = parseCalculatorMapContextProjectionV1(candidate);
    this._onProjection(this._projection);
    return this._projection;
  }

  _acceptCalculatorWorkspace(state) {
    if (this._disposed) {
      return;
    }
    if (state?.kind === "ready") {
      try {
        const payload = decodeCalculatorPresetPayload(state.current?.payload);
        this._calculatorWorkspace = {
          kind: "ready",
          revision: boundedString(
            state.current?.revisionId,
            "Calculator Current revision",
          ),
          fishingMastery: presetUnsignedNumber(
            payload.signals.mastery,
            "Calculator Current mastery",
            4_294_967_295,
          ),
          fishingLifeskillLevel: presetUnsignedDecimalString(
            payload.signals.lifeskill_level,
            "Calculator Current lifeskill_level",
            65_535,
          ),
        };
      } catch (_error) {
        this._calculatorWorkspace = { kind: "invalid" };
      }
    } else if (state?.kind === "missing") {
      this._calculatorWorkspace = { kind: "missing" };
    } else if (["uninitialized", "loading"].includes(state?.kind)) {
      this._calculatorWorkspace = { kind: "loading" };
    } else {
      this._calculatorWorkspace = { kind: repositoryFailureState(state?.kind) };
    }
    this._publish();
  }

  _acceptOverlayWorkspace(state) {
    if (this._disposed) {
      return;
    }
    if (state?.kind === "ready") {
      try {
        this._overlay = {
          state: "ready",
          workspaceRevision: boundedString(
            state.current?.revisionId,
            "Calculator overlay Current revision",
          ),
          document: boundedOverlayDocument(state.current?.payload),
          reason: null,
        };
      } catch (_error) {
        this._overlay = failedOverlay("invalid", "calculator-overlay-current-invalid");
      }
    } else if (state?.kind === "missing") {
      this._overlay = {
        state: "missing",
        workspaceRevision: null,
        document: null,
        reason: null,
      };
    } else if (["uninitialized", "loading"].includes(state?.kind)) {
      this._overlay = loadingOverlay();
    } else {
      const kind = repositoryFailureState(state?.kind);
      this._overlay = failedOverlay(kind, `calculator-overlay-current-${kind}`);
    }
    this._publish();
  }

  async refreshServerContext({ sourceRef = null } = {}) {
    if (this._disposed) {
      return Object.freeze({ kind: "superseded" });
    }
    const generation = this._requestGeneration + 1;
    this._requestGeneration = generation;
    this._request?.abort();
    const request = new AbortController();
    this._request = request;
    this._server = { kind: "loading", value: null };
    this._publish();
    try {
      const loaded = await this._loadServerContext({
        sourceRef,
        signal: request.signal,
      });
      if (
        this._disposed
        || request.signal.aborted
        || generation !== this._requestGeneration
      ) {
        return Object.freeze({ kind: "superseded" });
      }
      this._server = {
        kind: "ready",
        value: parseCalculatorLootConditionContextResponseV1(loaded),
      };
      return Object.freeze({ kind: "applied", projection: this._publish() });
    } catch (_error) {
      if (
        this._disposed
        || request.signal.aborted
        || generation !== this._requestGeneration
      ) {
        return Object.freeze({ kind: "superseded" });
      }
      this._server = { kind: "unavailable", value: null };
      return Object.freeze({ kind: "failed", projection: this._publish() });
    } finally {
      if (this._request === request) {
        this._request = null;
      }
    }
  }

  start() {
    if (this._disposed) {
      throw new Error("Calculator map context owner is disposed");
    }
    if (this._started) {
      return this;
    }
    this._started = true;
    this._unsubscribers = [
      this._workspace.subscribe(
        CALCULATOR_MAP_PRESET_COLLECTION_KEY,
        (state) => this._acceptCalculatorWorkspace(state),
      ),
      this._workspace.subscribe(
        CALCULATOR_MAP_OVERLAY_COLLECTION_KEY,
        (state) => this._acceptOverlayWorkspace(state),
      ),
    ];
    this.ready = Promise.all([
      Promise.resolve()
        .then(() => this._workspace.load(CALCULATOR_MAP_PRESET_COLLECTION_KEY))
        .then(
          (state) => this._acceptCalculatorWorkspace(state),
          () => this._acceptCalculatorWorkspace({ kind: "unavailable" }),
        ),
      Promise.resolve()
        .then(() => this._workspace.load(CALCULATOR_MAP_OVERLAY_COLLECTION_KEY))
        .then(
          (state) => this._acceptOverlayWorkspace(state),
          () => this._acceptOverlayWorkspace({ kind: "unavailable" }),
        ),
      this.refreshServerContext(),
    ]).then(() => this._projection);
    return this;
  }

  dispose() {
    if (this._disposed) {
      return;
    }
    this._disposed = true;
    this._requestGeneration += 1;
    this._request?.abort();
    this._request = null;
    for (const unsubscribe of this._unsubscribers.splice(0)) {
      unsubscribe?.();
    }
  }
}

export function createCalculatorMapContextProjectionV1Owner(options = {}) {
  return new CalculatorMapContextProjectionV1Owner(options).start();
}
